import { CheckCircleOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { Alert, Button, Tabs, Typography } from "antd";
import mergeClassNames from "common/mergeClassNames";
import AiMappingDataPreview from "components/molecules/AiMappingDataPreview";
import AiMappingSuggestions from "components/organisms/AiMappingSuggestions";
import React, { useMemo, useState } from "react";

const { Title, Text } = Typography;
const { TabPane } = Tabs;

interface AiMapping {
    sourceColumn: string;
    targetAttribute: string;
    confidence: number;
    reasoning: string;
    transformations: any[];
    sourceColumnExists: boolean;
    targetAttributeExists: boolean;
}

interface AiMappingWorkflowProps {
    mappings: AiMapping[];
    summary?: {
        totalMappings: number;
        highConfidenceMappings: number;
        mediumConfidenceMappings: number;
        lowConfidenceMappings: number;
    };
    sourceData?: Record<string, any>[];
    sourceColumns?: string[];
    loading?: boolean;
    error?: string | null;
    onRefresh?: () => void;
    onMappingChange?: (sourceColumn: string, newTarget: string) => void;
    targetOptions?: Array<{ label: string; value: string }>;
    className?: string;
}

const AiMappingWorkflow: React.FC<AiMappingWorkflowProps> = ({
    mappings = [],
    summary,
    sourceData = [],
    sourceColumns = [],
    loading = false,
    error = null,
    onRefresh,
    onMappingChange,
    targetOptions = [],
    className = "",
}) => {
    const [activeTab, setActiveTab] = useState("mappings");

    // Transform source data to show mapped columns
    const mappedData = useMemo(() => {
        if (!sourceData || !mappings || sourceData.length === 0) return [];

        return sourceData.map((row, index) => {
            const mappedRow: Record<string, any> = {};

            mappings.forEach((mapping) => {
                const sourceValue = row[mapping.sourceColumn];
                mappedRow[mapping.targetAttribute] = sourceValue;
            });

            return {
                key: `row-${index}`,
                ...mappedRow,
            };
        });
    }, [sourceData, mappings]);

    // Create columns for the data preview
    const previewColumns = useMemo(() => {
        if (!mappings) return [];

        return mappings.map((mapping) => ({
            title: `${mapping.targetAttribute}${mapping.targetAttributeExists ? "" : " (New)"}`,
            dataIndex: mapping.targetAttribute,
            key: mapping.targetAttribute,
            dataType: mapping.targetAttributeExists ? "String" : "String", // You can enhance this based on schema
            description: mapping.targetAttributeExists ? undefined : "New field",
        }));
    }, [mappings]);

    const getConfidenceScore = () => {
        if (!summary) return 0;
        const total = summary.totalMappings;
        if (total === 0) return 0;

        const highWeight = summary.highConfidenceMappings * 1;
        const mediumWeight = summary.mediumConfidenceMappings * 0.7;
        const lowWeight = summary.lowConfidenceMappings * 0.4;

        return Math.round(((highWeight + mediumWeight + lowWeight) / total) * 100);
    };

    const confidenceScore = getConfidenceScore();

    if (error) {
        return <Alert title="Error Loading AI Mapping Suggestions" description={error} type="error" showIcon className="mb-4" />;
    }

    return (
        <div className={mergeClassNames("ai-mapping-workflow", className)}>
            {/* Header */}
            <div className="mb-6">
                <div className="mb-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Title level={3} className="mb-0">
                            AI-Powered Data Mapping
                        </Title>
                        <InfoCircleOutlined className="text-lg text-blue-500" />
                    </div>
                    {onRefresh && (
                        <Button onClick={onRefresh} loading={loading} type="primary">
                            Refresh AI Suggestions
                        </Button>
                    )}
                </div>

                {/* Summary Stats */}
                {summary && (
                    <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-6">
                                <div className="text-center">
                                    <Text className="text-2xl font-bold text-blue-600">{confidenceScore}%</Text>
                                    <Text className="block text-blue-600">Overall Confidence</Text>
                                </div>
                                <div className="flex items-center gap-4 text-sm">
                                    <span className="text-green-600">
                                        <CheckCircleOutlined className="mr-1" />
                                        {summary.highConfidenceMappings} High Confidence
                                    </span>
                                    <span className="text-yellow-600">{summary.mediumConfidenceMappings} Medium Confidence</span>
                                    <span className="text-red-600">{summary.lowConfidenceMappings} Low Confidence</span>
                                </div>
                            </div>
                            <div className="text-right">
                                <Text className="block font-medium text-blue-600">{summary.totalMappings} Total Mappings</Text>
                                <Text className="text-sm text-blue-500">AI analyzed your data and schema</Text>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Main Content Tabs */}
            <Tabs activeKey={activeTab} onChange={setActiveTab} className="ai-mapping-tabs">
                <TabPane tab="Field Mappings" key="mappings" className="p-0">
                    <AiMappingSuggestions
                        mappings={mappings}
                        summary={summary}
                        loading={loading}
                        onRefresh={onRefresh}
                        onMappingChange={onMappingChange}
                        targetOptions={targetOptions}
                    />
                </TabPane>

                <TabPane tab="Data Preview" key="preview" className="p-0">
                    <AiMappingDataPreview data={mappedData} columns={previewColumns} loading={loading} onRefresh={onRefresh} />
                </TabPane>
            </Tabs>
        </div>
    );
};

export default AiMappingWorkflow;
